import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  MedicalServices as MedicalIcon,
  Medication as MedicationIcon,
  CheckCircle as CheckIcon,
  PlayArrow as PlayIcon,
  ExpandMore as ExpandIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Send as SubmitIcon,
  ArrowBack as BackIcon,
  AccessTime as TimeIcon,
  Badge as BadgeIcon,
  Home as HomeIcon,
  Favorite as HeartIcon,
  Thermostat as TempIcon,
  MonitorWeight as WeightIcon,
  Opacity as BloodIcon,
  Speed as PulseIcon,
  Air as OxygenIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchAppointmentById, acceptAppointment, startVisit, arriveAtLocation } from '../../store/slices/appointmentSlice';
import axios from 'axios';

interface Medication {
  id?: number;
  medicineName: string;
  dosage: string;
  frequency: string;
  durationText: string;
  instructions: string;
  quantity?: number;
}

interface ExaminationData {
  // Vitals
  bloodPressureSystolic: number | null;
  bloodPressureDiastolic: number | null;
  pulseRate: number | null;
  temperature: number | null;
  weight: number | null;
  height: number | null;
  oxygenSaturation: number | null;
  // Symptoms (MANDATORY)
  chiefComplaints: string;
  hasFever: boolean;
  hasCough: boolean;
  hasBodyPain: boolean;
  hasHeadache: boolean;
  customSymptoms: string;
  // Diagnosis (MANDATORY)
  provisionalDiagnosis: string;
  finalDiagnosis: string;
  treatmentPlan: string;
  // Remarks (MANDATORY)
  remarks: string;
  // Follow up
  followUpRequired: boolean;
  followUpDate: string;
  followUpInstructions: string;
  // Notes
  notes: string;
}

const initialExamination: ExaminationData = {
  bloodPressureSystolic: null,
  bloodPressureDiastolic: null,
  pulseRate: null,
  temperature: null,
  weight: null,
  height: null,
  oxygenSaturation: null,
  chiefComplaints: '',
  hasFever: false,
  hasCough: false,
  hasBodyPain: false,
  hasHeadache: false,
  customSymptoms: '',
  provisionalDiagnosis: '',
  finalDiagnosis: '',
  treatmentPlan: '',
  remarks: '',
  followUpRequired: false,
  followUpDate: '',
  followUpInstructions: '',
  notes: '',
};

const steps = [
  { label: 'Appointment Details', icon: <PersonIcon /> },
  { label: 'Start Visit', icon: <PlayIcon /> },
  { label: 'Medical Examination', icon: <MedicalIcon /> },
  { label: 'Prescription', icon: <MedicationIcon /> },
  { label: 'Complete Visit', icon: <CheckIcon /> },
];

/**
 * Doctor Visit Workflow Page
 * Unified page for complete home visit workflow
 *
 * @author Civilhealth Development Team
 */
const DoctorVisitWorkflowPage: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { currentAppointment, loading, workflowLoading, workflowError } = useSelector((state: RootState) => state.appointment);

  const [activeStep, setActiveStep] = useState(0);
  const [examination, setExamination] = useState<ExaminationData>(initialExamination);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [saving, setSaving] = useState(false);
  const [visitReportId, setVisitReportId] = useState<number | null>(null);
  const [prescriptionId, setPrescriptionId] = useState<number | null>(null);
  const [showMedicationDialog, setShowMedicationDialog] = useState(false);
  const [currentMedication, setCurrentMedication] = useState<Medication>({
    medicineName: '',
    dosage: '',
    frequency: '1-0-1',
    durationText: '5 Days',
    instructions: '',
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'GOVERNMENT_DOCTOR') {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Load appointment
  useEffect(() => {
    if (appointmentId) {
      dispatch(fetchAppointmentById(parseInt(appointmentId)));
    }
  }, [dispatch, appointmentId]);

  // Set active step based on appointment status
  useEffect(() => {
    if (currentAppointment) {
      const status = currentAppointment.status?.toUpperCase();
      console.log('Appointment status:', status); // Debug log

      switch (status) {
        case 'PENDING':
          setActiveStep(0); // Accept appointment
          break;
        case 'ACCEPTED':
          setActiveStep(1); // Start visit
          break;
        case 'IN_PROGRESS':
          setActiveStep(2); // Mark arrived
          break;
        case 'VISITED':
          setActiveStep(3); // Examination + Prescription
          break;
        case 'CLOSED':
          setActiveStep(5); // Done
          break;
        default:
          setActiveStep(0);
      }

      // Pre-fill chief complaints
      setExamination(prev => ({
        ...prev,
        chiefComplaints: currentAppointment.chiefComplaints || currentAppointment.reasonForVisit || '',
      }));
    }
  }, [currentAppointment]);

  const handleAccept = async () => {
    if (currentAppointment) {
      try {
        await dispatch(acceptAppointment(currentAppointment.id)).unwrap();
        // Refetch appointment to get updated status
        dispatch(fetchAppointmentById(currentAppointment.id));
      } catch (error) {
        console.error('Failed to accept:', error);
      }
    }
  };

  const handleStartVisit = async () => {
    if (currentAppointment) {
      try {
        await dispatch(startVisit({ appointmentId: currentAppointment.id })).unwrap();
        // Refetch appointment to get updated status
        dispatch(fetchAppointmentById(currentAppointment.id));
      } catch (error) {
        console.error('Failed to start visit:', error);
      }
    }
  };

  const handleArrive = async () => {
    if (currentAppointment) {
      try {
        await dispatch(arriveAtLocation({ appointmentId: currentAppointment.id })).unwrap();
        // Refetch appointment to get updated status
        dispatch(fetchAppointmentById(currentAppointment.id));
      } catch (error) {
        console.error('Failed to mark arrival:', error);
      }
    }
  };

  const handleExaminationChange = (field: keyof ExaminationData, value: any) => {
    setExamination(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveExamination = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/medical-examination`,
        {
          appointmentId: currentAppointment?.id,
          ...examination,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setVisitReportId(response.data.data.id);
      }
    } catch (error) {
      console.error('Failed to save examination:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMedication = () => {
    if (currentMedication.medicineName) {
      setMedications(prev => [...prev, { ...currentMedication }]);
      setCurrentMedication({
        medicineName: '',
        dosage: '',
        frequency: '1-0-1',
        durationText: '5 Days',
        instructions: '',
      });
      setShowMedicationDialog(false);
    }
  };

  const handleRemoveMedication = (index: number) => {
    setMedications(prev => prev.filter((_, i) => i !== index));
  };

  // Validation for mandatory fields
  const getValidationStatus = () => {
    const hasSymptoms = !!(examination.chiefComplaints || examination.customSymptoms ||
                          examination.hasFever || examination.hasCough ||
                          examination.hasBodyPain || examination.hasHeadache);
    const hasDiagnosis = !!examination.finalDiagnosis;
    const hasPrescription = medications.length > 0;
    const hasRemarks = !!examination.remarks;

    return {
      symptoms: hasSymptoms,
      diagnosis: hasDiagnosis,
      prescription: hasPrescription,
      remarks: hasRemarks,
      allComplete: hasSymptoms && hasDiagnosis && hasPrescription && hasRemarks,
    };
  };

  const validation = getValidationStatus();

  const handleSubmitVisit = async () => {
    // Validate all mandatory fields
    if (!validation.allComplete) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');

      // 1. Create examination with all mandatory fields
      const examResponse = await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/medical-examination`,
        {
          appointmentId: currentAppointment?.id,
          bloodPressure: examination.bloodPressureSystolic && examination.bloodPressureDiastolic
            ? `${examination.bloodPressureSystolic}/${examination.bloodPressureDiastolic}`
            : undefined,
          pulseRate: examination.pulseRate,
          temperature: examination.temperature,
          weight: examination.weight,
          height: examination.height,
          oxygenLevel: examination.oxygenSaturation,
          fever: examination.hasFever,
          cough: examination.hasCough,
          bodyPain: examination.hasBodyPain,
          headache: examination.hasHeadache,
          symptomsNotes: examination.customSymptoms,
          provisionalDiagnosis: examination.provisionalDiagnosis,
          finalDiagnosis: examination.finalDiagnosis,
          assessmentNotes: examination.remarks,
          clinicalFindings: examination.chiefComplaints,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 2. Submit examination
      if (examResponse.data.success) {
        await axios.post(
          `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/medical-examination/${examResponse.data.data.id}/submit`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // 3. Create prescription with all medicines
      if (medications.length > 0) {
        await axios.post(
          `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/prescriptions`,
          {
            appointmentId: currentAppointment?.id,
            visitReportId: examResponse.data?.data?.id,
            diagnosis: examination.finalDiagnosis,
            chiefComplaints: examination.chiefComplaints,
            bloodPressure: examination.bloodPressureSystolic && examination.bloodPressureDiastolic
              ? `${examination.bloodPressureSystolic}/${examination.bloodPressureDiastolic}`
              : undefined,
            pulseRate: examination.pulseRate,
            temperature: examination.temperature?.toString(),
            weight: examination.weight?.toString(),
            dosageInstructions: 'Take medicines as prescribed',
            dietInstructions: examination.treatmentPlan,
            lifestyleAdvice: '',
            precautions: '',
            medications: medications,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setActiveStep(5);
      setTimeout(() => {
        navigate('/doctor/appointments');
      }, 2000);
    } catch (error) {
      console.error('Failed to complete visit:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !currentAppointment) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return 'warning';
      case 'ACCEPTED': return 'info';
      case 'IN_PROGRESS': return 'primary';
      case 'VISITED': return 'secondary';
      case 'CLOSED': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'transparent' , color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/doctor/appointments')}
            sx={{ color: 'white' }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Home Visit Workflow
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Booking: {currentAppointment.bookingNumber}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={currentAppointment.statusDisplayName || currentAppointment.status}
          color={getStatusColor(currentAppointment.status) as any}
          sx={{ fontWeight: 600 }}
        />
      </Paper>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step) => (
            <Step key={step.label}>
              <StepLabel StepIconComponent={() => (
                <Avatar sx={{
                  bgcolor: steps.indexOf(step) < activeStep ? '#4caf50' :
                          steps.indexOf(step) === activeStep ? '#1a73e8' : '#e0e0e0',
                  color: steps.indexOf(step) <= activeStep ? 'white' : '#9e9e9e'
                }}>
                  {step.icon}
                </Avatar>
              )}>
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Error Alert */}
      {workflowError && (
        <Alert severity="error" sx={{ mb: 3 }}>{workflowError}</Alert>
      )}

      {/* Step Content */}
      <Grid container spacing={3}>
        {/* Left Column - Patient/Visit Info */}
        <Grid item xs={12} md={4}>
          {/* Patient Info Card */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              Patient Information
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {/* HIGH CONTRAST: Light background + Dark icon */}
              <Avatar sx={{ width: 56, height: 56, bgcolor: 'transparent' , border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                <PersonIcon sx={{ color: '#1D4ED8', fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h6">{currentAppointment.patientName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentAppointment.patientAge || 'N/A'} yrs | {currentAppointment.patientGender || 'N/A'}
                </Typography>
              </Box>
            </Box>
            {currentAppointment.familyMemberRelationship && (
              <Chip label={currentAppointment.familyMemberRelationship} size="small" color="primary" variant="outlined" />
            )}
          </Paper>

          {/* Officer Info Card */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BadgeIcon color="primary" />
              Officer Details
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>{currentAppointment.officerName}</Typography>
            <Typography variant="body2" color="text.secondary">{currentAppointment.officerDesignation}</Typography>
            <Typography variant="body2" color="text.secondary">{currentAppointment.officerDepartment}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2">{currentAppointment.officerMobile}</Typography>
            </Box>
          </Paper>

          {/* Visit Address Card */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              Visit Location
            </Typography>
            <Typography variant="body2">{currentAppointment.fullVisitAddress}</Typography>
            {currentAppointment.visitLandmark && (
              <Typography variant="body2" color="text.secondary">
                Landmark: {currentAppointment.visitLandmark}
              </Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<LocationIcon />}
              sx={{ mt: 1 }}
              onClick={() => {
                const address = encodeURIComponent(currentAppointment.fullVisitAddress || '');
                window.open(`https://www.google.com/maps/search/${address}`, '_blank');
              }}
            >
              Open in Maps
            </Button>
          </Paper>

          {/* Appointment Time */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon color="primary" />
              Schedule
            </Typography>
            <Typography variant="body1">{currentAppointment.appointmentDate}</Typography>
            <Typography variant="body2" color="text.secondary">
              Time: {currentAppointment.appointmentTime}
            </Typography>
          </Paper>
        </Grid>

        {/* Right Column - Workflow Steps */}
        <Grid item xs={12} md={8}>
          {/* Step 0: Accept Appointment */}
          {activeStep === 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Pending Appointment</Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This appointment is waiting for your acceptance.
              </Alert>

              {/* Chief Complaints */}
              <Paper sx={{ p: 2, bgcolor: 'transparent' , mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Chief Complaints</Typography>
                <Typography variant="body1">{currentAppointment.chiefComplaints || currentAppointment.reasonForVisit}</Typography>
              </Paper>

              {/* Special Notes */}
              {currentAppointment.specialNotes && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Special Notes:</Typography>
                  {currentAppointment.specialNotes}
                </Alert>
              )}

              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<CheckIcon />}
                onClick={handleAccept}
                disabled={workflowLoading}
                fullWidth
              >
                Accept Appointment
              </Button>
            </Paper>
          )}

          {/* Step 1: Start Visit */}
          {activeStep === 1 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Ready to Start Visit</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Appointment accepted. Click "Start Visit" when you begin traveling to the patient's location.
              </Alert>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, bgcolor: 'transparent' , textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">✓</Typography>
                    <Typography variant="body2">Accepted</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, bgcolor: 'transparent' , textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">⏳</Typography>
                    <Typography variant="body2">Waiting to Start</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<PlayIcon />}
                onClick={handleStartVisit}
                disabled={workflowLoading}
                fullWidth
              >
                Start Visit (Begin Travel)
              </Button>
            </Paper>
          )}

          {/* Step 2: In Progress - Mark Arrived */}
          {activeStep === 2 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Traveling to Patient</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Visit started. When you arrive at the patient's location, click "Mark Arrived" to begin examination.
              </Alert>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, bgcolor: 'transparent' , textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">✓</Typography>
                    <Typography variant="body2">Accepted</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, bgcolor: 'transparent' , textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">✓</Typography>
                    <Typography variant="body2">Started</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, bgcolor: 'transparent' , textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">⏳</Typography>
                    <Typography variant="body2">Traveling</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Button
                variant="contained"
                color="info"
                size="large"
                startIcon={<LocationIcon />}
                onClick={handleArrive}
                disabled={workflowLoading}
                fullWidth
              >
                Mark Arrived at Location
              </Button>
            </Paper>
          )}

          {/* Step 3+: Medical Examination (VISITED status) */}
          {activeStep >= 3 && activeStep < 5 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <MedicalIcon color="primary" />
                Medical Examination
              </Typography>

              {/* Vitals Section */}
              <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>Vital Signs</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="BP Systolic"
                        value={examination.bloodPressureSystolic || ''}
                        onChange={(e) => handleExaminationChange('bloodPressureSystolic', e.target.value ? parseInt(e.target.value) : null)}
                        InputProps={{ endAdornment: <Typography variant="body2">mmHg</Typography> }}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="BP Diastolic"
                        value={examination.bloodPressureDiastolic || ''}
                        onChange={(e) => handleExaminationChange('bloodPressureDiastolic', e.target.value ? parseInt(e.target.value) : null)}
                        InputProps={{ endAdornment: <Typography variant="body2">mmHg</Typography> }}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Pulse Rate"
                        value={examination.pulseRate || ''}
                        onChange={(e) => handleExaminationChange('pulseRate', e.target.value ? parseInt(e.target.value) : null)}
                        InputProps={{ endAdornment: <Typography variant="body2">bpm</Typography> }}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Temperature"
                        value={examination.temperature || ''}
                        onChange={(e) => handleExaminationChange('temperature', e.target.value ? parseFloat(e.target.value) : null)}
                        InputProps={{ endAdornment: <Typography variant="body2">°F</Typography> }}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Weight"
                        value={examination.weight || ''}
                        onChange={(e) => handleExaminationChange('weight', e.target.value ? parseFloat(e.target.value) : null)}
                        InputProps={{ endAdornment: <Typography variant="body2">kg</Typography> }}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="SpO2"
                        value={examination.oxygenSaturation || ''}
                        onChange={(e) => handleExaminationChange('oxygenSaturation', e.target.value ? parseInt(e.target.value) : null)}
                        InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Symptoms Section - MANDATORY */}
              <Accordion defaultExpanded sx={{ mb: 2, border: !validation.symptoms ? '2px solid #d32f2f' : 'none' }}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>
                    Symptoms *
                    {!validation.symptoms && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        required
                        label="Chief Complaints *"
                        value={examination.chiefComplaints}
                        onChange={(e) => handleExaminationChange('chiefComplaints', e.target.value)}
                        placeholder="Describe the patient's main complaints..."
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormControlLabel
                        control={<Checkbox checked={examination.hasFever} onChange={(e) => handleExaminationChange('hasFever', e.target.checked)} />}
                        label="Fever"
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormControlLabel
                        control={<Checkbox checked={examination.hasCough} onChange={(e) => handleExaminationChange('hasCough', e.target.checked)} />}
                        label="Cough"
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormControlLabel
                        control={<Checkbox checked={examination.hasBodyPain} onChange={(e) => handleExaminationChange('hasBodyPain', e.target.checked)} />}
                        label="Body Pain"
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormControlLabel
                        control={<Checkbox checked={examination.hasHeadache} onChange={(e) => handleExaminationChange('hasHeadache', e.target.checked)} />}
                        label="Headache"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Other Symptoms"
                        value={examination.customSymptoms}
                        onChange={(e) => handleExaminationChange('customSymptoms', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Diagnosis Section - MANDATORY */}
              <Accordion defaultExpanded sx={{ mb: 2, border: !validation.diagnosis ? '2px solid #d32f2f' : 'none' }}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>
                    Diagnosis *
                    {!validation.diagnosis && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Provisional Diagnosis"
                        value={examination.provisionalDiagnosis}
                        onChange={(e) => handleExaminationChange('provisionalDiagnosis', e.target.value)}
                        placeholder="Initial diagnosis based on symptoms..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Final Diagnosis *"
                        value={examination.finalDiagnosis}
                        onChange={(e) => handleExaminationChange('finalDiagnosis', e.target.value)}
                        error={!validation.diagnosis && examination.finalDiagnosis === ''}
                        helperText={!validation.diagnosis && examination.finalDiagnosis === '' ? 'Required to complete visit' : 'Confirmed diagnosis after examination'}
                        placeholder="Enter confirmed diagnosis..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Treatment Plan"
                        value={examination.treatmentPlan}
                        onChange={(e) => handleExaminationChange('treatmentPlan', e.target.value)}
                        placeholder="Recommended treatment approach..."
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Follow Up */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>Follow Up</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Checkbox checked={examination.followUpRequired} onChange={(e) => handleExaminationChange('followUpRequired', e.target.checked)} />}
                        label="Follow-up Required"
                      />
                    </Grid>
                    {examination.followUpRequired && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            type="date"
                            label="Follow-up Date"
                            value={examination.followUpDate}
                            onChange={(e) => handleExaminationChange('followUpDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Instructions"
                            value={examination.followUpInstructions}
                            onChange={(e) => handleExaminationChange('followUpInstructions', e.target.value)}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSaveExamination}
                disabled={saving}
                sx={{ mb: 2 }}
              >
                Save Examination Draft
              </Button>
            </Paper>
          )}

          {/* Prescription Section - MANDATORY */}
          {activeStep >= 3 && activeStep < 5 && (
            <Paper sx={{ p: 3, mt: 2, border: !validation.prescription ? '2px solid #d32f2f' : 'none' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MedicationIcon color="primary" />
                  Prescription *
                  {!validation.prescription && <Chip label="Required - Add at least 1 medicine" size="small" color="error" sx={{ ml: 1 }} />}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setShowMedicationDialog(true)}
                >
                  Add Medicine
                </Button>
              </Box>

              {medications.length === 0 ? (
                <Alert severity="warning">
                  <Typography variant="subtitle2">No medicines added yet</Typography>
                  <Typography variant="body2">At least one medicine is required to complete the visit.</Typography>
                </Alert>
              ) : (
                <List>
                  {medications.map((med, index) => (
                    <ListItem key={index} sx={{ bgcolor: 'transparent' , mb: 1, borderRadius: 1 }}>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 600 }}>
                            {med.medicineName} - {med.dosage}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2">
                            {med.frequency} | {med.durationText} | {med.instructions}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => handleRemoveMedication(index)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Quick Add Medications */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Quick Add:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['Paracetamol 500mg', 'Amoxicillin 250mg', 'Cetirizine 10mg', 'Omeprazole 20mg', 'Azithromycin 500mg'].map((med) => (
                    <Chip
                      key={med}
                      label={med}
                      onClick={() => {
                        const [name, dosage] = med.split(' ');
                        setMedications(prev => [...prev, {
                          medicineName: name,
                          dosage: dosage,
                          frequency: '1-0-1',
                          durationText: '5 Days',
                          instructions: 'Take after meals',
                        }]);
                      }}
                      clickable
                    />
                  ))}
                </Box>
              </Box>
            </Paper>
          )}

          {/* Remarks Section - MANDATORY */}
          {activeStep >= 3 && activeStep < 5 && (
            <Paper sx={{ p: 3, mt: 2, border: !validation.remarks ? '2px solid #d32f2f' : 'none' }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <MedicalIcon color="primary" />
                Remarks *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                required
                label="Case Remarks *"
                value={examination.remarks}
                onChange={(e) => handleExaminationChange('remarks', e.target.value)}
                placeholder="Enter case summary, observations, and recommendations..."
                error={!validation.remarks && examination.remarks === ''}
                helperText={!validation.remarks && examination.remarks === '' ? 'Required to complete visit' : ''}
              />
            </Paper>
          )}

          {/* Complete Visit Section - Mandatory Fields Validation */}
          {activeStep >= 3 && activeStep < 5 && (
            <Paper sx={{ p: 3, mt: 2, bgcolor: validation.allComplete ? '#e8f5e9' : '#fff3e0' }}>
              <Typography variant="h6" sx={{ mb: 2, color: validation.allComplete ? 'success.main' : 'warning.main' }}>
                {validation.allComplete ? '✅ Ready to Submit' : '⚠️ Complete Mandatory Fields'}
              </Typography>

              {/* Validation Checklist */}
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Chip
                      icon={validation.symptoms ? <CheckIcon /> : <span style={{width: 20}}></span>}
                      label="Symptoms"
                      color={validation.symptoms ? 'success' : 'default'}
                      variant={validation.symptoms ? 'filled' : 'outlined'}
                      sx={{ width: '100%', justifyContent: 'flex-start' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Chip
                      icon={validation.diagnosis ? <CheckIcon /> : <span style={{width: 20}}></span>}
                      label="Diagnosis"
                      color={validation.diagnosis ? 'success' : 'default'}
                      variant={validation.diagnosis ? 'filled' : 'outlined'}
                      sx={{ width: '100%', justifyContent: 'flex-start' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Chip
                      icon={validation.prescription ? <CheckIcon /> : <span style={{width: 20}}></span>}
                      label={`Prescription (${medications.length})`}
                      color={validation.prescription ? 'success' : 'default'}
                      variant={validation.prescription ? 'filled' : 'outlined'}
                      sx={{ width: '100%', justifyContent: 'flex-start' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Chip
                      icon={validation.remarks ? <CheckIcon /> : <span style={{width: 20}}></span>}
                      label="Remarks"
                      color={validation.remarks ? 'success' : 'default'}
                      variant={validation.remarks ? 'filled' : 'outlined'}
                      sx={{ width: '100%', justifyContent: 'flex-start' }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Missing Fields Alert */}
              {!validation.allComplete && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Please complete all mandatory fields before submitting:
                  <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                    {!validation.symptoms && <li>Symptoms - Enter chief complaints or select symptoms</li>}
                    {!validation.diagnosis && <li>Diagnosis - Enter final diagnosis</li>}
                    {!validation.prescription && <li>Prescription - Add at least one medicine</li>}
                    {!validation.remarks && <li>Remarks - Enter case remarks</li>}
                  </ul>
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {validation.allComplete
                    ? 'All mandatory fields completed. You can submit the report.'
                    : `${[!validation.symptoms, !validation.diagnosis, !validation.prescription, !validation.remarks].filter(Boolean).length} fields remaining`}
                </Typography>
                <Button
                  variant="contained"
                  color={validation.allComplete ? 'success' : 'inherit'}
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                  onClick={handleSubmitVisit}
                  disabled={saving || !validation.allComplete}
                  sx={{ opacity: validation.allComplete ? 1 : 0.5 }}
                >
                  {validation.allComplete ? 'Submit Report' : 'Complete All Fields'}
                </Button>
              </Box>
            </Paper>
          )}

          {/* Completed Message */}
          {activeStep === 5 && (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'transparent' }}>
              <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>Visit Completed Successfully!</Typography>
              <Typography variant="body1" color="text.secondary">
                Prescription and medical examination have been saved.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Redirecting to appointments...
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Add Medication Dialog */}
      <Dialog open={showMedicationDialog} onClose={() => setShowMedicationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Medicine</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Medicine Name *"
                value={currentMedication.medicineName}
                onChange={(e) => setCurrentMedication(prev => ({ ...prev, medicineName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Dosage"
                value={currentMedication.dosage}
                onChange={(e) => setCurrentMedication(prev => ({ ...prev, dosage: e.target.value }))}
                placeholder="e.g., 500mg"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={currentMedication.frequency}
                  label="Frequency"
                  onChange={(e) => setCurrentMedication(prev => ({ ...prev, frequency: e.target.value }))}
                >
                  <MenuItem value="1-0-0">Morning only (1-0-0)</MenuItem>
                  <MenuItem value="0-0-1">Night only (0-0-1)</MenuItem>
                  <MenuItem value="1-0-1">Morning & Night (1-0-1)</MenuItem>
                  <MenuItem value="1-1-1">Three times (1-1-1)</MenuItem>
                  <MenuItem value="1-1-1-1">Four times (1-1-1-1)</MenuItem>
                  <MenuItem value="SOS">SOS (As needed)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Duration"
                value={currentMedication.durationText}
                onChange={(e) => setCurrentMedication(prev => ({ ...prev, durationText: e.target.value }))}
                placeholder="e.g., 5 Days"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Instructions"
                value={currentMedication.instructions}
                onChange={(e) => setCurrentMedication(prev => ({ ...prev, instructions: e.target.value }))}
                placeholder="e.g., After meals"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMedicationDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddMedication} disabled={!currentMedication.medicineName}>
            Add Medicine
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorVisitWorkflowPage;
